import { Divider, Group, Stack, Table, Text, TextInput, Textarea } from "@mantine/core";
import {
  ICheckItem,
  IChecklistItem,
  IChecklistItemRow,
  IStampSettings,
  TChecklistItemStatus,
} from "@mctypes/checklist.types";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
  IOverleveringChecklistQuestionRow,
  IOverleveringChecklistReport,
  IOverleveringChecklistRow,
} from "@mcapps/shared/reportSchemas/startupChecklistSchema";
import StampCheck from "../../checklist/StampCheck";
import { setEditedDataInRedux } from "../reportSlice";
import { ensureOverleveringChecklistReport, getOverleveringChecklistTitle } from "./StartupChecklistReportUtils";

const STARTUP_STAMP_SETTINGS: IStampSettings = {
  yes: { color: "#00975f", icon: "check", text: "JA" },
  no: { color: "#dc3545", icon: "close", text: "NEI" },
  ir: { color: "#f8992e", icon: "ban", text: "IR" },
  na: { color: "#0000004F", icon: "circle", text: "-" },
  uav: { color: "#4085b6ff", icon: "exclamation-triangle", text: "UAV" },
};

const toChecklistStatus = (answer: IOverleveringChecklistQuestionRow["answer"]): TChecklistItemStatus => {
  switch (answer) {
    case "JA":
      return "YES";
    case "NEI":
      return "NO";
    case "IR":
      return "IR";
    default:
      return "N/A";
  }
};

const fromChecklistStatus = (status: TChecklistItemStatus): IOverleveringChecklistQuestionRow["answer"] => {
  switch (status) {
    case "YES":
      return "JA";
    case "NO":
      return "NEI";
    case "IR":
      return "IR";
    default:
      return null;
  }
};

const StartupChecklistReport: React.FC<{
  onUpdateEditData?: (data: IOverleveringChecklistReport) => void;
}> = ({ onUpdateEditData }) => {
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.adminReducer.project);
  const rawEditData = useAppSelector(
    (state) => state.reportReducer.editedData[901],
  ) as IOverleveringChecklistReport | null;

  const editData = rawEditData == null ? null : ensureOverleveringChecklistReport(rawEditData);

  if (!editData) return null;

  const updateData = (updated: IOverleveringChecklistReport) => {
    dispatch(
      setEditedDataInRedux({
        code: 901,
        data: updated,
      }),
    );
    onUpdateEditData?.(updated);
  };

  React.useEffect(() => {
    const projectNo = project?.maconomy?.projectNo;
    const projectName = project?.maconomy?.name;

    if (projectNo == null && !projectName) return;

    const nextOppdragsnummer =
      editData.overleveringChecklistMeta.oppdragsnummer || (projectNo != null ? String(projectNo) : "");
    const nextOppdragsnavn = editData.overleveringChecklistMeta.oppdragsnavn || projectName || "";

    if (
      nextOppdragsnummer === editData.overleveringChecklistMeta.oppdragsnummer &&
      nextOppdragsnavn === editData.overleveringChecklistMeta.oppdragsnavn
    ) {
      return;
    }

    updateData({
      ...editData,
      overleveringChecklistMeta: {
        ...editData.overleveringChecklistMeta,
        oppdragsnummer: nextOppdragsnummer,
        oppdragsnavn: nextOppdragsnavn,
      },
    });
  }, [editData, project?.maconomy?.name, project?.maconomy?.projectNo]);

  const updateMeta = (path: keyof IOverleveringChecklistReport["overleveringChecklistMeta"], value: string) => {
    updateData({
      ...editData,
      overleveringChecklistMeta: {
        ...editData.overleveringChecklistMeta,
        [path]: value,
      },
    });
  };

  const updateRow = (nr: string, updater: (row: IOverleveringChecklistRow) => IOverleveringChecklistRow) => {
    updateData({
      ...editData,
      rows: editData.rows.map((row) => (row.nr === nr ? updater(row) : row)),
    });
  };

  const answerCell = (row: IOverleveringChecklistQuestionRow) => {
    const checkItem: ICheckItem = {
      status: toChecklistStatus(row.answer),
      updatedBy: null,
      comment: {
        createdBy: null,
        text: "",
        createdDate: null,
      },
    };

    const originalItem = {} as IChecklistItem;
    const checklistRow = {
      _id: row.nr,
      originalItem,
    } as IChecklistItemRow;

    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <StampCheck
          disabled={false}
          row={checklistRow}
          checkItem={checkItem}
          stampSettings={STARTUP_STAMP_SETTINGS}
          includeIr={true}
          statusDisabled={true}
          toggle={(_, __, newValue) =>
            updateRow(row.nr, (currentRow) =>
              currentRow.kind === "QUESTION" ? { ...currentRow, answer: fromChecklistStatus(newValue) } : currentRow,
            )
          }
        />
      </div>
    );
  };

  return (
    <Stack p="md" style={{ maxWidth: 1200, margin: "0 auto" }}>
      <Text size="xl" fw={700} ta="center">
        1. {getOverleveringChecklistTitle(editData.overleveringChecklistType)} (obligatorisk)
      </Text>

      <Divider
        my="sm"
        label={
          <Text fz="md" fw={600}>
            Generell informasjon
          </Text>
        }
      />

      <Stack gap="xs">
        <Group grow align="flex-start">
          <TextInput
            label="Tittel"
            value={editData.overleveringChecklistMeta.title}
            onChange={(e) => updateMeta("title", e.currentTarget.value)}
          />
        </Group>
        <Group grow align="flex-start">
          <TextInput
            label="Oppdragsnummer"
            value={editData.overleveringChecklistMeta.oppdragsnummer}
            onChange={(e) => updateMeta("oppdragsnummer", e.currentTarget.value)}
          />
          <TextInput
            label="Oppdragsnavn"
            value={editData.overleveringChecklistMeta.oppdragsnavn}
            onChange={(e) => updateMeta("oppdragsnavn", e.currentTarget.value)}
          />
        </Group>
      </Stack>

      <Divider
        my="sm"
        label={
          <Text fz="md" fw={600}>
            Sjekkliste
          </Text>
        }
      />

      <Table
        striped
        withTableBorder
        withColumnBorders
        highlightOnHover
        styles={{
          thead: {
            position: "static", // 👈 disables sticky
          },
          th: {
            position: "static", // 👈 ensures headers are not sticky
            top: "auto",
            paddingTop: 6,
            paddingBottom: 6,
          },
          td: {
            paddingTop: 4,
            paddingBottom: 4,
          },
        }}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={50}>NR.</Table.Th>
            <Table.Th>KONTROLLSPØRSMÅL / KRAV</Table.Th>
            <Table.Th w={110}>JA / NEI / IR</Table.Th>
            <Table.Th w="32%">KOMMENTAR / TILTAK</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {editData.rows.map((row) =>
            row.kind === "SECTION" ? (
              <Table.Tr key={`${row.kind}-${row.nr}-${row.text}`}>
                <Table.Td fw={700}>{row.nr}</Table.Td>
                <Table.Td colSpan={3}>
                  <Text fw={700} style={{ whiteSpace: "pre-wrap" }}>
                    {row.text}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              <Table.Tr key={`${row.kind}-${row.nr}`}>
                <Table.Td>{row.nr}</Table.Td>
                <Table.Td>
                  <Text style={{ whiteSpace: "pre-wrap" }}>{row.text}</Text>
                </Table.Td>
                <Table.Td>{answerCell(row)}</Table.Td>
                <Table.Td>
                  <Textarea
                    autosize
                    minRows={1}
                    value={row.comment}
                    onChange={(e) =>
                      updateRow(row.nr, (currentRow) =>
                        currentRow.kind === "QUESTION" ? { ...currentRow, comment: e.currentTarget.value } : currentRow,
                      )
                    }
                  />
                </Table.Td>
              </Table.Tr>
            ),
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

export default StartupChecklistReport;
